import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { accountDeletionService } from '@/services/auth/accountDeletionService';
import DeleteAccountDialog from './DeleteAccountDialog';

interface DeleteAccountCardProps {
  familyMembers: any[];
  isFamilyAdmin: boolean;
  email?: string;
}

export const DeleteAccountCard: React.FC<DeleteAccountCardProps> = ({
  familyMembers,
  isFamilyAdmin,
  email
}) => {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const hasOtherFamilyMembers = familyMembers.length > 1;
  const cannotDelete = isFamilyAdmin && hasOtherFamilyMembers;

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const result = await accountDeletionService.deleteAccount();
      
      if (result.success) {
        toast.success('Account deleted successfully');
        setIsDialogOpen(false);
        navigate('/');
      } else {
        toast.error(result.error || 'Failed to delete account');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenDialog = () => {
    if (cannotDelete) return;
    setIsDialogOpen(true);
  };

  return (
    <Card className="border-destructive/20">
      <CardHeader>
        <CardTitle className="text-destructive flex items-center gap-2">
          <Trash2 className="w-5 h-5" />
          Delete Account
        </CardTitle>
        <CardDescription>
          Permanently delete your account and all associated data. This action cannot be undone.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {cannotDelete && (
          <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-destructive">Cannot delete account</p>
              <p className="text-sm text-muted-foreground mt-1">
                You are the family admin with other members. Please remove all family members first or leave the family before deleting your account.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <h4 className="font-medium">What will be deleted:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Your profile and account information</li>
            <li>• All your recipes and meal plans</li>
            <li>• Your shopping lists and meal notes</li>
            <li>• Your recipe feedback and activity history</li>
            <li>• All associated data and preferences</li>
          </ul>
        </div>

        <Button 
          variant="destructive" 
          disabled={cannotDelete}
          className="w-full"
          onClick={handleOpenDialog}
        >
          Delete My Account
        </Button>
        
        <DeleteAccountDialog 
          isOpen={isDialogOpen}
          isDeleting={isDeleting}
          onOpenChange={setIsDialogOpen}
          onConfirmDelete={handleDeleteAccount}
          email={email}
        />
      </CardContent>
    </Card>
  );
};